import { apiFetch } from "./client";

/** Filters shared by every LLM usage endpoint. Empty strings are omitted. */
export interface LlmUsageFilters {
  days?: 7 | 30 | 90;
  from?: string;
  to?: string;
  companyId?: string;
  agentId?: string;
  workflowId?: string;
  workflowRunId?: string;
  workflowStepId?: string;
  model?: string;
  provider?: string;
  providerResource?: string;
  providerAccountId?: string;
  lane?: string;
  outcome?: string;
}

export const BREAKDOWN_DIMS = ["tenant", "agent", "workflow", "step", "model", "resource", "account", "lane"] as const;
export type BreakdownDim = (typeof BREAKDOWN_DIMS)[number];

export interface UsageTotals {
  calls: number;
  turns: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  reasoningTokens: number;
  toolCalls: number;
  costCents: number;
  unpricedCalls: number;
  errors: number;
  avgLatencyMs: number;
  cacheWritePerCall: number;
  cacheHitShare: number;
}

export interface DailyPoint {
  day: string;
  calls: number;
  costCents: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  errors: number;
}

export interface UsageSummary {
  range: { from: string; to: string };
  totals: UsageTotals;
  daily: DailyPoint[];
}

export interface BreakdownRow extends UsageTotals {
  key: string;
  label: string;
  meta: Record<string, string | null>;
}

export interface CallRow {
  id: string;
  ts: string;
  companyId: string;
  agentId: string | null;
  agentSlug: string;
  turnId: string | null;
  roundIndex: number;
  lane: string | null;
  workflowRunId: string | null;
  workflowId: string | null;
  workflowName: string | null;
  workflowStepId: string | null;
  sessionId: string | null;
  humanUpn: string | null;
  provider: string | null;
  model: string | null;
  modelRequested: string | null;
  providerResource: string | null;
  providerAccountId: string | null;
  engine: string | null;
  requestId: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  cacheWrite5mTokens: number | null;
  cacheWrite1hTokens: number | null;
  reasoningTokens: number | null;
  toolCalls: number;
  stopReason: string | null;
  outcome: string | null;
  error: string | null;
  latencyMs: number | null;
  costCents: number | null;
  priced: boolean | null;
}

export interface CandidateRow {
  kind: "failures" | "fallbacks" | "idle-background" | "cache-write-heavy" | "long-turns";
  label: string;
  detail: string;
  calls: number;
  costCents: number;
  meta: Record<string, string | null>;
}

export interface FilterOptions {
  tenants: Array<{ id: string; name: string }>;
  agents: Array<{ id: string; name: string; slug: string; companyId: string }>;
  models: Array<{ provider: string | null; model: string }>;
  resources: string[];
  lanes: string[];
}

export interface TokenBucket {
  requests: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

export interface ProclawBucket extends TokenBucket {
  costCents: number;
  unpricedCalls: number;
}

export type ReconStatus = "ok" | "drift" | "azure-only" | "proclaw-only" | "no-azure";

export interface ReconRow {
  day: string;
  deployment: string;
  proclaw: ProclawBucket;
  azure: TokenBucket | null;
  azureCostCents: number | null;
  drift: { requests: number | null; prompt: number | null; output: number | null };
  status: ReconStatus;
}

export interface ResourceRecon {
  resource: string;
  resourceId: string | null;
  azure: { ok: boolean; error: string | null; hint: string | null };
  totals: {
    proclaw: ProclawBucket;
    azure: TokenBucket | null;
    azureCostCents: number | null;
    promptCoverage: number | null;
    outputCoverage: number | null;
  };
  rows: ReconRow[];
}

export interface Reconciliation {
  range: { from: string; to: string };
  threshold: number;
  resources: ResourceRecon[];
}

export interface BudgetRow {
  scope: "tenant" | "agent";
  companyId: string;
  companyName: string;
  agentId: string | null;
  name: string;
  budgetCents: number | null;
  spentCents: number;
  unpricedCalls: number;
  pct: number | null;
  projectedCents: number | null;
  alerted: number[];
}

export interface BudgetAlertRow {
  id: string;
  companyId: string;
  agentId: string | null;
  threshold: number;
  spentCents: number;
  budgetCents: number;
  delivered: Array<{ target: string; ok: boolean; error?: string }> | null;
  createdAt: string;
}

export interface Budgets {
  month: string;
  rows: BudgetRow[];
  alerts: BudgetAlertRow[];
}

export interface AccountAgentUsage {
  agentId: string | null;
  agentSlug: string;
  name: string;
  calls: number;
  tokens: number;
  costCents: number;
}

export interface AccountUsageRow {
  accountId: string;
  companyId: string;
  companyName: string;
  provider: string;
  label: string | null;
  status: string;
  plan: string | null;
  monthlyFeeCents: number | null;
  feeForWindowCents: number | null;
  cooling: boolean;
  cooldownUntil: number | null;
  lastError: string | null;
  lastUsedAt: string | null;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costCents: number;
  unpricedCalls: number;
  errors: number;
  authErrors: number;
  limitErrors: number;
  lastCallAt: string | null;
  value: number | null;
  agents: AccountAgentUsage[];
}

function qs(filters: LlmUsageFilters, extra: Record<string, string | number | undefined> = {}): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...filters, ...extra })) {
    if (v !== undefined && v !== "" && v !== null) params.set(k, String(v));
  }
  return params.toString();
}

export const llmUsageApi = {
  summary: (f: LlmUsageFilters) => apiFetch<UsageSummary>(`/admin/llm-usage/summary?${qs(f)}`),
  breakdown: (f: LlmUsageFilters, dim: BreakdownDim) =>
    apiFetch<{ dim: BreakdownDim; rows: BreakdownRow[] }>(`/admin/llm-usage/breakdown?${qs(f, { dim })}`),
  calls: (f: LlmUsageFilters, opts: { limit?: number; before?: string } = {}) =>
    apiFetch<{ calls: CallRow[]; nextBefore: string | null }>(`/admin/llm-usage/calls?${qs(f, opts)}`),
  candidates: (f: LlmUsageFilters) => apiFetch<{ rows: CandidateRow[] }>(`/admin/llm-usage/candidates?${qs(f)}`),
  filters: (f: LlmUsageFilters) => apiFetch<FilterOptions>(`/admin/llm-usage/filters?${qs(f)}`),
  reconciliation: (f: Pick<LlmUsageFilters, "days" | "from" | "to" | "providerResource">) =>
    apiFetch<Reconciliation>(`/admin/llm-usage/reconciliation?${qs(f)}`),
  budgets: () => apiFetch<Budgets>("/admin/llm-usage/budgets"),
  accounts: (f: Pick<LlmUsageFilters, "days" | "from" | "to" | "companyId" | "agentId">) =>
    apiFetch<{ range: { from: string; to: string }; rows: AccountUsageRow[] }>(`/admin/llm-usage/accounts?${qs(f)}`),
  setAccountPlan: (body: { accountId: string; plan?: string | null; monthlyFeeCents?: number | null }) =>
    apiFetch<{ ok: true }>("/admin/llm-usage/accounts", { method: "PATCH", body: JSON.stringify(body) }),
  setBudget: (body: { companyId?: string; agentId?: string; budgetMonthlyCents: number | null }) =>
    apiFetch<{ ok: true }>("/admin/llm-usage/budgets", { method: "PATCH", body: JSON.stringify(body) }),
  runBudgetMonitor: () => apiFetch<{ updated: number; alerts: number }>("/admin/llm-usage/budgets/run", { method: "POST" }),
};
